"use client";

import { Button } from "@package/subframe/components/Button";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { TextArea } from "@package/subframe/components/TextArea";
import { Tooltip } from "@package/subframe/components/Tooltip";
import { TopBar } from "@package/subframe/components/TopBar";
import {
  FeatherAlertCircle,
  FeatherAlertTriangle,
  FeatherArrowLeft,
  FeatherCalendar,
  FeatherCheck,
  FeatherCheckCircle,
  FeatherCircleFadingPlus,
  FeatherClock,
  FeatherEdit2,
  FeatherPaperclip,
  FeatherPlus,
  FeatherRefreshCw,
  FeatherUsers,
  FeatherWifiOff,
  FeatherX,
} from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { isPast } from "date-fns";
import { Camera, ImageIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { ConfirmAcknowledgeChecklistDialog } from "@/components/checklists/ConfirmAcknowledgeChecklistDialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { OfflineImage } from "@/components/ui/offline-image";
import { Spinner } from "@/components/ui/spinner";
import { UserMenu, UserMenuTrigger } from "@/components/user-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useOrganisation } from "@/contexts/OrganisationContext";
import { useDeleteFileControllerDeleteFile } from "@/generated/files/files";
import { useListActionsBySqQueryControllerListBySq } from "@/generated/follow-up-actions/follow-up-actions";
import { useAcknowledgeChecklistControllerAcknowledge } from "@/generated/scheduled-questionnaires/scheduled-questionnaires";
import {
  type DisplayPhoto,
  type InitialAnswerFile,
  useOfflineChecklist,
} from "@/hooks/use-offline-checklist";
import {
  trackChecklistCompleted,
  trackChecklistReviewed,
  trackChecklistStarted,
  trackEvidenceAdded,
  trackSessionEnded,
} from "@/lib/analytics";
import { signOut } from "@/lib/auth";
import { acknowledgeErrorMessage } from "@/lib/checklist-manager";
import { isFailedAnswer, QuestionAnswerInput } from "@/lib/checklist-utils";
import { formatUkDate } from "@/lib/date-utils";
import { clearAllOfflineData } from "@/lib/offline/db";
import { canAcknowledgeFailedChecks } from "@/lib/roles";

interface Question {
  id: string;
  questionnaireId: string;
  questionText: string;
  questionType: string;
  options: unknown;
  isRequired: boolean;
  isHidden?: boolean;
  orderIndex: number;
  validationRules: unknown;
}

interface Questionnaire {
  id: string;
  templateId: string;
  title: string;
  description: string | null;
  status: string;
  availableUntil: string;
  missBehaviour?: string;
  questions: Question[];
}

interface AnsweredByUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

interface InitialAnswer {
  id?: string;
  questionId: string;
  answerValue: string | number | boolean | string[] | null;
  notes?: string;
  files?: InitialAnswerFile[];
  answeredByUser?: AnsweredByUser;
}

export interface ChecklistAcknowledgement {
  acknowledgedAt: string;
  source: string;
  acknowledgedBy?: { id: string; firstName?: string; lastName?: string };
}

interface ScheduledQuestionnaireExecutionProps {
  scheduledQuestionnaireId: string;
  questionnaire: Questionnaire;
  nurseryId: string;
  userId: string;
  dueTime?: string;
  location?: string;
  initialAnswers?: InitialAnswer[];
  onClose: () => void;
  onRefetchAnswers?: () => Promise<void>;
  isPreview?: boolean;
  /** Failed checks awaiting acknowledgement — drives the red banner (NAU-71). */
  needsAcknowledgement?: boolean;
  failedCheckCount?: number;
  acknowledgements?: ChecklistAcknowledgement[];
}

type AnswerValue = "yes" | "no" | "na" | number | string;

export function ScheduledQuestionnaireExecution({
  scheduledQuestionnaireId,
  questionnaire,
  nurseryId,
  userId,
  dueTime,
  location: _location,
  initialAnswers,
  onClose,
  onRefetchAnswers,
  isPreview,
  needsAcknowledgement = false,
  failedCheckCount = 0,
  acknowledgements = [],
}: ScheduledQuestionnaireExecutionProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, userRoleType } = useAuth();

  const isManager =
    userRoleType === "PLATFORM_ADMIN" ||
    userRoleType === "ORG_ADMIN" ||
    userRoleType === "NURSERY_MANAGER";

  const { nurseryRole } = useNursery();
  const { isOrgAdmin } = useOrganisation();
  const canAcknowledge = canAcknowledgeFailedChecks({
    isPlatformAdmin: userRoleType === "PLATFORM_ADMIN",
    isOrgAdmin,
    nurseryRole,
  });

  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [isInitializing, setIsInitializing] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [pendingPhotoQuestionId, setPendingPhotoQuestionId] = useState<
    string | null
  >(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [completedAt, setCompletedAt] = useState<Date | null>(null);
  const [previewImage, setPreviewImage] = useState<{
    url: string;
    alt: string;
  } | null>(null);
  const checklistStartTimeRef = useRef<number>(Date.now());
  const hasTrackedStartRef = useRef(false);

  // Use offline-first hook - scheduledQuestionnaireId is the instanceId
  const {
    completion,
    answers,
    photos,
    syncStatus,
    saveAnswer,
    savePhoto,
    deletePhoto,
    finalizeCompletion,
    retryFailed: _retryFailed,
    getOrCreateCompletion,
  } = useOfflineChecklist(
    scheduledQuestionnaireId,
    nurseryId,
    userId,
    initialAnswers,
  );

  // Mutation to delete server files
  const deleteServerFile = useDeleteFileControllerDeleteFile();

  // Initialize completion on mount
  useEffect(() => {
    async function init() {
      await getOrCreateCompletion();
      setIsInitializing(false);
    }
    init();
  }, [getOrCreateCompletion]);

  const questionTypeById = useMemo(
    () => new Map(questionnaire.questions.map((q) => [q.id, q.questionType])),
    [questionnaire.questions],
  );

  // Track checklist_started when user opens an incomplete checklist
  // Track checklist_reviewed when manager opens a completed checklist
  useEffect(() => {
    if (isInitializing || hasTrackedStartRef.current) return;

    const isAlreadyCompleted =
      completion?.completedAt !== null ||
      questionnaire.status === "COMPLETED" ||
      questionnaire.status === "COMPLETED_LATE";

    if (!isAlreadyCompleted) {
      // Track start for incomplete checklists
      trackChecklistStarted({
        checklistInstanceId: scheduledQuestionnaireId,
        checklistTemplateId: questionnaire.templateId,
        isOverdue:
          questionnaire.status === "EXPIRED" ||
          isPast(new Date(questionnaire.availableUntil)),
      });
      hasTrackedStartRef.current = true;
      checklistStartTimeRef.current = Date.now();
    } else {
      // Track review for completed checklists (only for admins/managers)
      const canReview =
        userRoleType === "PLATFORM_ADMIN" ||
        userRoleType === "ORG_ADMIN" ||
        userRoleType === "NURSERY_MANAGER";

      if (canReview) {
        const failedItemCount = Array.from(answers.entries()).filter(
          ([qId, a]) => isFailedAnswer(a.answer, questionTypeById.get(qId)),
        ).length;

        let evidenceCount = 0;
        photos.forEach((questionPhotos) => {
          evidenceCount += questionPhotos.length;
        });

        trackChecklistReviewed({
          checklistInstanceId: scheduledQuestionnaireId,
          hadFailedItems: failedItemCount > 0,
          evidenceCount,
        });
      }
      hasTrackedStartRef.current = true;
    }
  }, [
    isInitializing,
    completion,
    questionnaire,
    scheduledQuestionnaireId,
    answers,
    photos,
    userRoleType,
    questionTypeById,
  ]);

  const isCompleted =
    completion?.completedAt !== null ||
    questionnaire.status === "COMPLETED" ||
    questionnaire.status === "COMPLETED_LATE";

  // HARD_MISS items that are past deadline are read-only (cannot be started or continued)
  const isHardMissExpired =
    questionnaire.missBehaviour === "HARD_MISS" &&
    (questionnaire.status === "EXPIRED" ||
      isPast(new Date(questionnaire.availableUntil)));

  // Combined read-only flag: completed OR hard-miss expired OR preview mode
  const isReadOnly = isCompleted || isHardMissExpired || isPreview;

  // Fetch existing follow-up actions for this checklist (only when completed and user is manager)
  const { data: existingActionsData } =
    useListActionsBySqQueryControllerListBySq(scheduledQuestionnaireId, {
      query: { enabled: isCompleted && isManager },
    });
  const existingActions =
    (existingActionsData?.data as unknown as Array<{
      id: string;
      answerId: string;
      description: string;
      status: string;
    }>) || [];

  // Acknowledging clears every failed check on the checklist at once (NAU-71).
  const acknowledgeMutation = useAcknowledgeChecklistControllerAcknowledge();
  const [showAcknowledgeDialog, setShowAcknowledgeDialog] = useState(false);

  const refreshAfterAcknowledge = useCallback(async () => {
    setShowAcknowledgeDialog(false);
    // The detail view is served from IndexedDB, so a query invalidation alone
    // wouldn't refresh it — refetch first, then clear the count-bearing queries.
    await onRefetchAnswers?.();
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey?.[0];
        return (
          typeof key === "string" &&
          (key.includes("/scheduled-questionnaires") ||
            key.includes("/dashboard"))
        );
      },
      refetchType: "all",
    });
  }, [onRefetchAnswers, queryClient]);

  const handleAcknowledge = useCallback(async () => {
    try {
      await acknowledgeMutation.mutateAsync({ scheduledQuestionnaireId });
      await refreshAfterAcknowledge();
      toast.success("Failed checks acknowledged");
    } catch (err) {
      const { message, alreadyAcknowledged } = acknowledgeErrorMessage(err);
      // Already cleared elsewhere — the view is stale, not the request wrong.
      if (alreadyAcknowledged) {
        await refreshAfterAcknowledge();
        toast.info(message);
        return;
      }
      toast.error(message);
    }
  }, [acknowledgeMutation, scheduledQuestionnaireId, refreshAfterAcknowledge]);

  // Sort questions by orderIndex
  const sortedQuestions = useMemo(() => {
    return [...questionnaire.questions]
      .filter((q) => !q.isHidden)
      .sort((a, b) => a.orderIndex - b.orderIndex);
  }, [questionnaire.questions]);

  // Convert offline answers to local format for UI
  const localAnswers = useMemo(() => {
    const map = new Map<string, { answerValue: AnswerValue; notes?: string }>();
    answers.forEach((answer, itemId) => {
      map.set(itemId, {
        answerValue: answer.answer,
        notes: answer.notes ?? undefined,
      });
    });
    return map;
  }, [answers]);

  const handleAnswer = async (questionId: string, answer: AnswerValue) => {
    if (isReadOnly) return;

    const existingAnswer = localAnswers.get(questionId);

    // Save to IndexedDB immediately (non-blocking)
    try {
      await saveAnswer(questionId, answer, existingAnswer?.notes ?? null);
    } catch (error) {
      console.error("Failed to save answer:", error);
      toast.error("Failed to save answer");
    }
  };

  const handleSaveNote = async (questionId: string, notes: string) => {
    if (isReadOnly) return;

    const existingAnswer = localAnswers.get(questionId);
    if (!existingAnswer) return;

    // Track evidence added when adding a new note (not updating existing)
    const isNewNote = notes && !existingAnswer.notes;

    try {
      await saveAnswer(questionId, existingAnswer.answerValue, notes);

      if (isNewNote) {
        trackEvidenceAdded({
          checklistInstanceId: scheduledQuestionnaireId,
          evidenceType: "comment",
          associatedWithFailedItem: isFailedAnswer(
            existingAnswer.answerValue,
            questionTypeById.get(questionId),
          ),
        });
      }

      toast.success("Note saved");
    } catch (error) {
      console.error("Failed to save note:", error);
      toast.error("Failed to save note");
    }
  };

  const handleTakePhoto = useCallback(
    (questionId: string) => {
      if (isReadOnly) return;
      const answer = localAnswers.get(questionId);
      if (!answer) {
        toast.error("Please answer the question first");
        return;
      }
      setPendingPhotoQuestionId(questionId);
      cameraInputRef.current?.click();
    },
    [isReadOnly, localAnswers],
  );

  const handleChooseFromGallery = useCallback(
    (questionId: string) => {
      if (isReadOnly) return;
      const answer = localAnswers.get(questionId);
      if (!answer) {
        toast.error("Please answer the question first");
        return;
      }
      setPendingPhotoQuestionId(questionId);
      fileInputRef.current?.click();
    },
    [isReadOnly, localAnswers],
  );

  const MAX_PHOTOS_PER_QUESTION = 2;

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0 || !pendingPhotoQuestionId) return;

      const currentPhotos = photos.get(pendingPhotoQuestionId) || [];
      const remainingSlots = MAX_PHOTOS_PER_QUESTION - currentPhotos.length;

      if (remainingSlots <= 0) {
        toast.error(`Maximum ${MAX_PHOTOS_PER_QUESTION} photos allowed`);
        e.target.value = "";
        setPendingPhotoQuestionId(null);
        return;
      }

      const filesToAdd = Array.from(files).slice(0, remainingSlots);

      try {
        for (const file of filesToAdd) {
          await savePhoto(pendingPhotoQuestionId, file);
        }

        // Track evidence added
        const answer = localAnswers.get(pendingPhotoQuestionId);
        trackEvidenceAdded({
          checklistInstanceId: scheduledQuestionnaireId,
          evidenceType: "photo",
          associatedWithFailedItem: isFailedAnswer(
            answer?.answerValue,
            questionTypeById.get(pendingPhotoQuestionId),
          ),
        });

        toast.success(
          filesToAdd.length === 1
            ? "Photo added"
            : `${filesToAdd.length} photos added`,
        );
      } catch (error) {
        console.error("Failed to save photo:", error);
        toast.error(
          error instanceof Error ? error.message : "Failed to save photo",
        );
      } finally {
        setPendingPhotoQuestionId(null);
        // Reset input value so the same file can be selected again
        e.target.value = "";
      }
    },
    [
      pendingPhotoQuestionId,
      savePhoto,
      photos,
      localAnswers,
      scheduledQuestionnaireId,
      questionTypeById,
    ],
  );

  const handleDeletePhoto = useCallback(
    async (questionId: string, photo: DisplayPhoto) => {
      try {
        // Check if there's a server record to delete
        // - Pure server file: has downloadUrl but no blob, use photo.id
        // - Local file with server record: has blob AND serverId (created before GCS upload failed)
        const serverFileId = photo.serverId || (!photo.blob ? photo.id : null);

        if (serverFileId) {
          // Delete from server
          await deleteServerFile.mutateAsync({ fileId: serverFileId });
          // Refetch answers to update file list
          await queryClient.invalidateQueries({ queryKey: ["answers"] });
        }

        // Always remove from local state (and IndexedDB if it's a local photo)
        await deletePhoto(questionId, photo.id);
        toast.success("Photo removed");
      } catch (error) {
        console.error("Failed to delete photo:", error);
        toast.error("Failed to remove photo");
      }
    },
    [deletePhoto, deleteServerFile, queryClient],
  );

  // Refetch answers when sync completes (pending count goes to 0)
  const prevPendingCount = useRef(syncStatus.pendingCount);
  useEffect(() => {
    // If we had pending items and now we don't, sync completed
    if (
      prevPendingCount.current > 0 &&
      syncStatus.pendingCount === 0 &&
      !syncStatus.isSyncing
    ) {
      queryClient.invalidateQueries({ queryKey: ["answers"] });
    }
    prevPendingCount.current = syncStatus.pendingCount;
  }, [syncStatus.pendingCount, syncStatus.isSyncing, queryClient]);

  const getPhotoUrl = useCallback((photo: DisplayPhoto) => {
    // Server photos have downloadUrl, local photos have blob
    if (photo.downloadUrl) {
      return photo.downloadUrl;
    }
    if (photo.blob) {
      return URL.createObjectURL(photo.blob);
    }
    return "";
  }, []);

  const handleSubmit = () => {
    // Check all required questions are answered
    const unansweredRequired = sortedQuestions.filter(
      (q) => q.isRequired && !localAnswers.has(q.id),
    );

    if (unansweredRequired.length > 0) {
      toast.error(
        `Please answer all ${unansweredRequired.length} required items before submitting`,
      );
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmDialog(false);

    try {
      // Finalize completion - sync service will call the complete API
      await finalizeCompletion();
      // Invalidate scheduled questionnaires query to refetch data
      await queryClient.invalidateQueries({
        queryKey: [`/v1/nurseries/${nurseryId}/scheduled-questionnaires`],
      });

      // Calculate completion metrics for analytics
      const completionDurationSeconds = Math.floor(
        (Date.now() - checklistStartTimeRef.current) / 1000,
      );
      const failedItemCount = Array.from(localAnswers.entries()).filter(
        ([qId, a]) => isFailedAnswer(a.answerValue, questionTypeById.get(qId)),
      ).length;
      // Count total photos across all questions
      let evidenceItemCount = 0;
      photos.forEach((questionPhotos) => {
        evidenceItemCount += questionPhotos.length;
      });

      // Track checklist completion
      trackChecklistCompleted({
        checklistInstanceId: scheduledQuestionnaireId,
        checklistTemplateId: questionnaire.templateId,
        completedOnTime: questionnaire.status !== "EXPIRED",
        completionDurationSeconds,
        completedOffline: !syncStatus.isOnline,
        failedItemCount,
        evidenceItemCount,
      });

      // Show success dialog
      setCompletedAt(new Date());
      setShowSuccessDialog(true);
    } catch (error) {
      console.error("Failed to submit:", error);
      toast.error("Failed to submit checklist");
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessDialog(false);
    onClose();
  };

  const handleSignOut = async () => {
    const sessionStartTime = sessionStorage.getItem("session_start_time");
    const sessionDurationSeconds = sessionStartTime
      ? Math.floor((Date.now() - parseInt(sessionStartTime, 10)) / 1000)
      : 0;
    trackSessionEnded("manual", sessionDurationSeconds);
    sessionStorage.removeItem("session_start_time");
    await clearAllOfflineData();
    await signOut();
    window.location.href = "/login";
  };

  const answeredCount = localAnswers.size;
  const totalCount = sortedQuestions.length;
  const requiredCount = sortedQuestions.filter((q) => q.isRequired).length;
  const answeredRequiredCount = sortedQuestions.filter(
    (q) => q.isRequired && localAnswers.has(q.id),
  ).length;

  // Count issues (number of 'no' answers)
  const issuesCount = Array.from(localAnswers.entries()).filter(([qId, a]) =>
    isFailedAnswer(a.answerValue, questionTypeById.get(qId)),
  ).length;

  // Get user display name and data for avatar
  const userName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ") ||
      user.email ||
      "Unknown"
    : "Unknown";

  const userData = {
    name: userName,
    email: user?.email || "",
    avatar: "",
  };

  // Gather unique users who contributed answers
  const uniqueContributors = useMemo(() => {
    const contributorMap = new Map<string, { name: string; email: string }>();

    // Add users from initialAnswers (answers already saved to server)
    if (initialAnswers) {
      for (const answer of initialAnswers) {
        if (answer.answeredByUser) {
          const { id, firstName, lastName, email } = answer.answeredByUser;
          const displayName =
            [firstName, lastName].filter(Boolean).join(" ") ||
            email ||
            "Unknown";
          contributorMap.set(id, { name: displayName, email });
        }
      }
    }

    // If no contributors from server answers, use current user as fallback
    if (contributorMap.size === 0 && user) {
      contributorMap.set(user.uid || user.id || "current", {
        name: userName,
        email: user.email || "",
      });
    }

    return Array.from(contributorMap.values());
  }, [user, userName, initialAnswers]);

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="flex w-full h-full flex-col bg-white">
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileSelected}
      />
      <input
        type="file"
        ref={cameraInputRef}
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFileSelected}
      />

      {/* TopBar with progress */}
      <div className="flex w-full items-start shrink-0 bg-white z-10">
        <TopBar
          menuIcon={<FeatherArrowLeft />}
          onMenuClick={onClose}
          header={questionnaire.title}
          showWifi={!syncStatus.isOnline}
          rightFirst={<UserMenuTrigger user={userData} />}
          userMenu={<UserMenu user={userData} />}
          rightThird={
            !syncStatus.isOnline ? (
              <SubframeCore.Tooltip.Provider>
                <SubframeCore.Tooltip.Root>
                  <SubframeCore.Tooltip.Trigger asChild={true}>
                    <IconWithBackground
                      className="h-11 w-11 flex-none"
                      variant="neutral"
                      size="medium"
                      icon={<FeatherWifiOff />}
                      square={true}
                    />
                  </SubframeCore.Tooltip.Trigger>
                  <SubframeCore.Tooltip.Portal>
                    {/*
                     * No `asChild` here — it throws `React.Children.only` when
                     * the tooltip opens, because duplicate installed copies of
                     * @radix-ui/react-slot break Radix's Slottable identity
                     * check. See the same note on the Task Manager page.
                     */}
                    <SubframeCore.Tooltip.Content
                      side="bottom"
                      align="center"
                      sideOffset={4}
                    >
                      <Tooltip>Offline</Tooltip>
                    </SubframeCore.Tooltip.Content>
                  </SubframeCore.Tooltip.Portal>
                </SubframeCore.Tooltip.Root>
              </SubframeCore.Tooltip.Provider>
            ) : undefined
          }
          variant="inner-pages"
          dueLabel={dueTime}
          count={`${answeredCount} of ${totalCount}`}
          progress={
            totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0
          }
        />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex w-full max-w-[1024px] m-auto flex-col items-start gap-8 px-4 py-6">
          {/* Completed banner — red while failed checks still need acknowledging (NAU-71) */}
          {isCompleted && !needsAcknowledgement && (
            <div className="flex w-full items-center gap-2 rounded-sm bg-success-100 px-4 py-3 mobile:flex-row mobile:flex-nowrap mobile:gap-3 mobile:bg-success-50">
              <FeatherCheck className="text-heading-3 font-heading-3 text-default-font mobile:text-heading-2 mobile:font-heading-2 mobile:text-success-900" />
              <span className="text-heading-2 font-heading-2 text-default-font mobile:text-success-900">
                This checklist is completed
              </span>
            </div>
          )}

          {needsAcknowledgement && (
            <div className="flex w-full flex-col items-start gap-3 rounded-sm bg-error-100 px-4 py-3 mobile:bg-error-50">
              <div className="flex w-full items-center gap-2">
                <FeatherAlertTriangle className="text-heading-3 font-heading-3 text-default-font mobile:text-heading-2 mobile:font-heading-2 mobile:text-error-900" />
                <span className="text-heading-2 font-heading-2 text-default-font mobile:text-error-900">
                  {failedCheckCount} failed{" "}
                  {failedCheckCount === 1 ? "check needs" : "checks need"}{" "}
                  acknowledgement
                </span>
              </div>
              {canAcknowledge && (
                <Button
                  className="h-10 flex-none"
                  variant="brand-primary"
                  icon={<FeatherCheckCircle />}
                  disabled={!syncStatus.isOnline}
                  onClick={() => setShowAcknowledgeDialog(true)}
                >
                  Acknowledge failed checks
                </Button>
              )}
            </div>
          )}

          {/* Acknowledgement history (NAU-71) */}
          {acknowledgements.length > 0 && (
            <div className="flex w-full flex-col items-start gap-2 rounded-sm border border-solid border-neutral-border px-4 py-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Acknowledgement history
              </span>
              {acknowledgements.map((acknowledgement) => {
                const name = [
                  acknowledgement.acknowledgedBy?.firstName,
                  acknowledgement.acknowledgedBy?.lastName,
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <span
                    key={acknowledgement.acknowledgedAt}
                    className="text-caption font-caption text-subtext-color"
                  >
                    {acknowledgement.source === "AUTO_FOLLOW_UP_COVERAGE"
                      ? "Automatically acknowledged — every failed check has a follow-up action"
                      : `Acknowledged by ${name || "a manager"}`}
                    {` on ${formatUkDate(new Date(acknowledgement.acknowledgedAt), "d MMM yyyy, HH:mm")}`}
                  </span>
                );
              })}
            </div>
          )}

          {/* Hard miss expired banner */}
          {isHardMissExpired && !isCompleted && (
            <div className="flex w-full items-center gap-2 rounded-sm bg-error-100 px-4 py-3 mobile:flex-row mobile:flex-nowrap mobile:gap-3 mobile:bg-error-50">
              <FeatherX className="text-heading-3 font-heading-3 text-default-font mobile:text-heading-2 mobile:font-heading-2 mobile:text-error-900" />
              <span className="text-heading-2 font-heading-2 text-default-font mobile:text-error-900">
                This checklist was missed and can no longer be completed
              </span>
            </div>
          )}

          {/* Preview mode banner */}
          {isPreview && !isCompleted && !isHardMissExpired && (
            <div className="flex w-full items-center gap-2 rounded-sm bg-neutral-100 px-4 py-3 mobile:flex-row mobile:flex-nowrap mobile:gap-3">
              <FeatherCalendar className="text-heading-3 font-heading-3 text-neutral-600 mobile:text-heading-2 mobile:font-heading-2" />
              <span className="text-heading-2 font-heading-2 text-neutral-600">
                This checklist is not yet active
              </span>
            </div>
          )}

          {/* Section header */}
          <div className="flex w-full flex-col items-start pt-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Questions
            </span>
          </div>

          {/* Questions list */}
          {sortedQuestions.map((question, index) => {
            const answer = localAnswers.get(question.id);
            const isExpanded = expandedItem === question.id;
            const questionPhotos = photos.get(question.id) || [];
            const hasEvidence = questionPhotos.length > 0 || answer?.notes;
            const canAddPhotos =
              questionPhotos.length < MAX_PHOTOS_PER_QUESTION;

            return (
              <div key={question.id} className="w-full">
                {/* Question item */}
                <div className="flex w-full flex-col items-start gap-4">
                  {/* Question header with number */}
                  <div className="flex w-full items-start gap-3">
                    <div className="flex items-center gap-2 pt-1">
                      <div className="flex h-8 w-8 flex-none flex-col items-center justify-center gap-2 overflow-hidden rounded-full bg-neutral-100 relative">
                        <span className="line-clamp-1 w-full font-['Manrope'] text-[14px] font-[600] leading-[14px] text-neutral-800 text-center absolute">
                          {index + 1}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 pt-2 mobile:h-auto mobile:grow mobile:shrink-0 mobile:basis-0">
                      <span className="text-heading-3 font-heading-3 text-neutral-900">
                        {question.questionText}
                      </span>
                    </div>
                  </div>

                  {/* Answer input */}
                  <div className="flex w-full flex-col items-start gap-3">
                    <QuestionAnswerInput
                      questionType={question.questionType}
                      answerValue={answer?.answerValue}
                      onAnswer={(value) => handleAnswer(question.id, value)}
                      readOnly={isReadOnly}
                    />

                    {/* Add note and photo button - only when answered and no evidence */}
                    {answer && !isExpanded && !hasEvidence && !isReadOnly && (
                      <Button
                        className="h-12 w-full flex-none"
                        variant="neutral-primary"
                        size="large"
                        onClick={() => {
                          setExpandedItem(question.id);
                          setNoteText(answer.notes || "");
                        }}
                      >
                        Add a note and photo
                      </Button>
                    )}

                    {/* Edit mode - note input and photo grid */}
                    {isExpanded && answer && (
                      <div className="flex w-full flex-col items-start gap-3 rounded-md bg-neutral-50 px-4 py-4">
                        <span className="text-body-bold font-body-bold text-neutral-900">
                          Add a note or photo
                        </span>
                        <TextArea
                          className="h-auto w-full flex-none"
                          variant="outline"
                          label=""
                          helpText=""
                        >
                          <TextArea.Input
                            placeholder="Add an optional note..."
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            disabled={isReadOnly}
                          />
                        </TextArea>

                        {/* Photo grid */}
                        {!isReadOnly && (
                          <div className="items-start gap-2 grid grid-cols-8 mobile:grid mobile:grid-cols-3">
                            {/* Add photo button */}
                            {canAddPhotos && (
                              <div
                                className="flex flex-col items-center justify-center gap-1 rounded-sm border border-dashed border-neutral-300 bg-white aspect-square cursor-pointer hover:bg-neutral-50 min-h-[96px] max-h-[112px]"
                                onClick={() =>
                                  handleChooseFromGallery(question.id)
                                }
                              >
                                <Camera className="h-5 w-5 text-subtext-color" />
                                <span className="text-body-bold font-body-bold text-subtext-color text-center text-xs">
                                  Add photo
                                </span>
                              </div>
                            )}
                            {/* Existing photos */}
                            {questionPhotos.map((photo, photoIndex) => (
                              <div
                                key={photo.id}
                                className="relative aspect-square min-h-[96px] max-h-[112px]"
                              >
                                <div className="w-full h-full bg-muted rounded-sm overflow-hidden">
                                  <OfflineImage
                                    src={getPhotoUrl(photo)}
                                    alt={`Photo ${photoIndex + 1}`}
                                    className="w-full h-full object-cover"
                                    uploadStatus={
                                      photo.uploadStatus as
                                        | "PENDING"
                                        | "UPLOADING"
                                        | "COMPLETED"
                                        | "FAILED"
                                        | undefined
                                    }
                                  />
                                </div>
                                {/* Delete button */}
                                <button
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-neutral-600 hover:bg-neutral-700 rounded-full flex items-center justify-center"
                                  onClick={() =>
                                    handleDeletePhoto(question.id, photo)
                                  }
                                >
                                  <FeatherX className="h-3 w-3 text-white" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Photo action buttons */}
                        {!isReadOnly && (
                          <div className="flex w-full gap-2">
                            <Button
                              className="h-12 grow shrink-0 basis-0"
                              variant="neutral-secondary"
                              size="large"
                              icon={<Camera className="h-4 w-4" />}
                              onClick={() => handleTakePhoto(question.id)}
                              disabled={!canAddPhotos}
                            >
                              Take photo
                            </Button>
                            <Button
                              className="h-12 grow shrink-0 basis-0"
                              variant="neutral-secondary"
                              size="large"
                              icon={<ImageIcon className="h-4 w-4" />}
                              onClick={() =>
                                handleChooseFromGallery(question.id)
                              }
                              disabled={!canAddPhotos}
                            >
                              Gallery
                            </Button>
                          </div>
                        )}

                        {/* Done button */}
                        <Button
                          className="h-12 w-full flex-none"
                          variant="brand-primary"
                          size="large"
                          onClick={async () => {
                            // Save note if changed
                            if (noteText !== (answer?.notes || "")) {
                              handleSaveNote(question.id, noteText);
                            }
                            setExpandedItem(null);
                            // Refetch answers to get updated photos from server
                            if (onRefetchAnswers) {
                              await onRefetchAnswers();
                            }
                          }}
                        >
                          Done
                        </Button>
                      </div>
                    )}

                    {/* Evidence attached view */}
                    {hasEvidence && !isExpanded && (
                      <div className="flex w-full flex-col items-start gap-3 rounded-md bg-neutral-50 px-4 py-4">
                        <div className="flex w-full items-start justify-between">
                          <div className="flex items-center gap-2">
                            <FeatherPaperclip className="text-body font-body text-neutral-900" />
                            <span className="text-body-bold font-body-bold text-neutral-900">
                              Evidence attached
                            </span>
                          </div>
                          {!isReadOnly && (
                            <Button
                              variant="neutral-primary"
                              icon={<FeatherEdit2 />}
                              onClick={() => {
                                setExpandedItem(question.id);
                                setNoteText(answer?.notes || "");
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </div>

                        {/* Photo thumbnails */}
                        {questionPhotos.length > 0 && (
                          <div className="w-full items-start gap-2 grid grid-cols-8 mobile:grid mobile:grid-cols-3">
                            {questionPhotos.map((photo, photoIndex) => (
                              <div
                                key={photo.id}
                                className="relative aspect-square min-h-[96px] max-h-[112px] cursor-pointer"
                                onClick={() =>
                                  setPreviewImage({
                                    url: getPhotoUrl(photo),
                                    alt: `Photo ${photoIndex + 1}`,
                                  })
                                }
                              >
                                <div className="w-full h-full bg-muted rounded-sm overflow-hidden">
                                  <OfflineImage
                                    src={getPhotoUrl(photo)}
                                    alt={`Photo ${photoIndex + 1}`}
                                    className="w-full h-full object-cover"
                                    uploadStatus={
                                      photo.uploadStatus as
                                        | "PENDING"
                                        | "UPLOADING"
                                        | "COMPLETED"
                                        | "FAILED"
                                        | undefined
                                    }
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Note display */}
                        {answer?.notes && (
                          <div className="flex flex-col items-start gap-3 rounded-sm bg-neutral-200 px-2.5 py-2">
                            <span className="text-body font-body text-neutral-900">
                              {answer.notes}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Follow-up action section for failed items (managers only, completed checklists) */}
                  {isCompleted &&
                    isManager &&
                    isFailedAnswer(
                      answer?.answerValue,
                      question.questionType,
                    ) &&
                    (() => {
                      const serverAnswer = initialAnswers?.find(
                        (ia) => ia.questionId === question.id,
                      );
                      const serverAnswerId = serverAnswer?.id;
                      const actionsForThis = serverAnswerId
                        ? existingActions.filter(
                            (a) => a.answerId === serverAnswerId,
                          )
                        : [];
                      return (
                        <div className="flex w-full flex-col items-start gap-2">
                          {actionsForThis.map((action) => (
                            <div
                              key={action.id}
                              className="flex w-full items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2"
                            >
                              <FeatherCheck className="text-body font-body text-amber-600 flex-none" />
                              <div className="flex grow flex-col">
                                <span className="text-caption-bold font-caption-bold text-amber-700">
                                  Action created
                                </span>
                                <span className="text-caption font-caption text-amber-600 line-clamp-1">
                                  {action.description}
                                </span>
                              </div>
                            </div>
                          ))}
                          {serverAnswerId && (
                            <Button
                              className="h-10 w-full flex-none"
                              variant="neutral-primary"
                              icon={<FeatherPlus />}
                              onClick={() =>
                                navigate({
                                  to: "/follow-up-actions/create",
                                  search: {
                                    scheduledQuestionnaireId,
                                    answerId: serverAnswerId,
                                    nurseryId,
                                    questionText: question.questionText,
                                    checklistName: questionnaire.title,
                                  },
                                })
                              }
                            >
                              Create follow-up action
                            </Button>
                          )}
                        </div>
                      );
                    })()}
                </div>

                {/* Divider between questions */}
                {index < sortedQuestions.length - 1 && (
                  <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border mt-8" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="flex w-full items-center justify-center gap-4 border-t border-solid border-neutral-200 bg-white px-4 py-3 shrink-0">
        {/* Sync status indicators */}
        {!isReadOnly && syncStatus.errorCount > 0 && (
          <div className="flex items-center gap-2 text-error-700">
            <FeatherAlertCircle className="text-body font-body" />
            <span className="text-body font-body">
              Sync failed ({syncStatus.errorCount})
            </span>
          </div>
        )}
        {!isReadOnly &&
          syncStatus.isOnline &&
          syncStatus.pendingCount > 0 &&
          syncStatus.errorCount === 0 && (
            <div className="flex items-center gap-2 text-subtext-color">
              <FeatherRefreshCw className="text-body font-body animate-spin" />
              <span className="text-body font-body">
                Syncing ({syncStatus.pendingCount})
              </span>
            </div>
          )}
        {!isReadOnly && !syncStatus.isOnline && (
          <div className="flex items-center gap-2">
            <FeatherWifiOff className="text-body font-body text-subtext-color" />
            <span className="text-body font-body text-subtext-color">
              Offline auto-save
            </span>
          </div>
        )}
        <Button
          className="h-12 grow shrink-0 basis-0"
          variant="brand-primary"
          size="extra-large"
          icon={null}
          onClick={isReadOnly ? undefined : handleSubmit}
          disabled={isReadOnly || answeredRequiredCount < requiredCount}
        >
          {isPreview
            ? "Not yet active"
            : isCompleted
              ? "Locked"
              : isHardMissExpired
                ? "Missed"
                : "Complete"}
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmAcknowledgeChecklistDialog
        open={showAcknowledgeDialog}
        onOpenChange={setShowAcknowledgeDialog}
        checklistName={questionnaire.title}
        failedCheckCount={failedCheckCount}
        isSaving={acknowledgeMutation.isPending}
        onConfirm={handleAcknowledge}
      />

      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white mobile:flex-col mobile:flex-nowrap mobile:gap-6 mobile:px-4 mobile:py-4">
            <div className="flex max-w-[448px] flex-col items-center gap-6 p-4 mobile:w-full mobile:grow mobile:shrink-0 mobile:basis-0 mobile:items-center mobile:justify-center">
              <div className="flex flex-col items-center gap-6 mobile:h-auto mobile:w-full mobile:flex-none">
                <IconWithLogoBackground
                  variant="neutral"
                  size="x-large"
                  icon={<FeatherCircleFadingPlus />}
                />
                <div className="flex flex-col items-center gap-2">
                  <span className="w-full text-heading-2 font-heading-2 text-default-font text-center mobile:text-primary-navy-800">
                    Ready to complete?
                  </span>
                  <span className="text-body font-body text-subtext-color text-center mobile:text-center mobile:px-4">
                    Once you mark this checklist as complete, you won&apos;t be
                    able to edit your answers. Make sure everything looks good
                    before confirming.
                  </span>
                </div>
              </div>
              <div className="grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-3 rounded-lg bg-neutral-50 px-4 py-4 mobile:h-auto mobile:w-full mobile:flex-none">
                <span className="text-body font-body text-subtext-color">
                  Checklist
                </span>
                <span className="break-all text-right text-body-bold font-body-bold text-neutral-900">
                  {questionnaire.title}
                </span>
                <span className="text-body font-body text-subtext-color">
                  Completed by
                </span>
                <span className="break-all text-right text-body-bold font-body-bold text-neutral-900">
                  {userName}
                </span>
                <span className="text-body font-body text-subtext-color">
                  Issues identified
                </span>
                <span className="break-all text-right text-body-bold font-body-bold text-neutral-900">
                  {issuesCount}
                </span>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4 mobile:items-center mobile:justify-center mobile:px-0 mobile:py-0">
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={() => setShowConfirmDialog(false)}
              >
                Go back to checklist
              </Button>
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="brand-primary"
                size="extra-large"
                onClick={handleConfirmSubmit}
              >
                Yes, mark as complete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
          <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white mobile:flex-col mobile:flex-nowrap mobile:items-center mobile:justify-center mobile:gap-6 mobile:px-4 mobile:py-4">
            <div className="flex flex-col items-center gap-6 p-4 mobile:w-full mobile:grow mobile:shrink-0 mobile:basis-0 mobile:flex-col mobile:flex-nowrap mobile:items-center mobile:justify-center mobile:gap-6">
              <div className="flex flex-col items-center gap-6 mobile:h-auto mobile:w-full mobile:flex-none mobile:flex-col mobile:flex-nowrap mobile:gap-6">
                <IconWithLogoBackground variant="success" size="x-large" />
                <div className="flex flex-col items-center gap-2 mobile:flex-col mobile:flex-nowrap mobile:gap-2">
                  <span className="text-heading-2 font-heading-2 text-default-font mobile:text-primary-navy-800 mobile:text-center">
                    {questionnaire.title}
                  </span>
                  <span className="text-body font-body text-subtext-color mobile:text-center">
                    Checklist completed successfully
                  </span>
                </div>
              </div>
              <div className="flex w-80 flex-col items-start gap-4 rounded-lg bg-neutral-50 px-4 py-4 mobile:h-auto mobile:w-full mobile:flex-none">
                <div className="flex w-full items-center gap-2">
                  <FeatherCalendar className="text-body font-body text-neutral-900" />
                  <span className="text-body font-body text-neutral-900">
                    {completedAt
                      ? formatUkDate(completedAt, "EEEE, d MMMM yyyy")
                      : ""}
                  </span>
                </div>
                <div className="flex w-full items-center gap-2">
                  <FeatherClock className="text-body font-body text-neutral-900" />
                  <span className="text-body font-body text-neutral-900">
                    {completedAt ? formatUkDate(completedAt, "HH:mm") : ""}
                  </span>
                </div>
                <div className="flex w-full items-start gap-2 mobile:items-center mobile:justify-start">
                  <FeatherUsers className="text-body font-body text-neutral-900" />
                  <span className="text-body font-body text-neutral-900">
                    Participants:{" "}
                    {uniqueContributors.map((c) => c.name).join(", ")}
                  </span>
                </div>
                <div className="flex w-full items-start gap-2 mobile:items-center mobile:justify-start">
                  <FeatherCheck className="text-body font-body text-neutral-900" />
                  <span className="text-body font-body text-neutral-900">
                    Completed by {userName}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4 mobile:items-center mobile:justify-center mobile:px-0 mobile:py-0">
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="neutral-secondary"
                size="extra-large"
                onClick={handleSignOut}
              >
                Log out
              </Button>
              <Button
                className="h-14 w-80 flex-none mobile:h-14 mobile:w-full mobile:flex-none"
                variant="brand-primary"
                size="extra-large"
                onClick={handleSuccessClose}
              >
                Back to home
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewImage !== null}
        onOpenChange={(open) => !open && setPreviewImage(null)}
      >
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/90 border-none">
          <div className="relative flex items-center justify-center w-full h-full min-h-[50vh]">
            <button
              className="absolute top-2 right-2 z-10 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center cursor-pointer"
              onClick={() => setPreviewImage(null)}
            >
              <FeatherX className="text-white" />
            </button>
            {previewImage && (
              <img
                src={previewImage.url}
                alt={previewImage.alt}
                className="max-w-full max-h-[85vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
